import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Table, Space, Tooltip, Button, PageHeader, Tag, Result, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, PlusCircleOutlined } from '@ant-design/icons';
import {
  handleApiSuccess,
  handleApiError,
  confirmDelete,
  accessControls,
} from 'utils';
import {
  timeTrackerUserGroupsListService,
  timeTrackerUserGroupDeleteService,
} from 'services/time_tracker';

export default function TimeTrackerUserGroupsList() {
  const history = useHistory();
  const [permissions, setPermissions] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);

  useEffect(() => {
    accessControls
      .fetch()
      .then(setPermissions)
      .catch(() => setPermissions({}));
  }, []);

  const columns = [
    {
      title: 'Group name',
      dataIndex: 'group_name',
      key: 'group_name',
      sorter: (a, b) =>
        (a.group_name || '').localeCompare(b.group_name || '', undefined, {
          sensitivity: 'base',
        }),
    },
    {
      title: 'Members',
      dataIndex: 'members',
      key: 'members',
      render: (members) =>
        members && members.length
          ? members.map((u) => (
              <Tag key={u._id}>
                {u.display_name ||
                  [u.first_name, u.last_name].filter(Boolean).join(' ') ||
                  u.email}
              </Tag>
            ))
          : '—',
    },
    {
      title: 'Action',
      key: 'action',
      width: 140,
      render: (_, record) => (
        <Space size="small">
          {accessControls.can(permissions, 'TimeTrackerUserGroup.update:any') && (
            <Tooltip title="Edit">
              <Button
                type="primary"
                shape="circle"
                size="small"
                icon={<EditOutlined />}
                onClick={() =>
                  history.push('/time-tracker/user-groups/update/' + record._id)
                }
              />
            </Tooltip>
          )}
          {accessControls.can(permissions, 'TimeTrackerUserGroup.delete:any') && (
            <Tooltip title="Delete">
              <Button
                shape="circle"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => deleteRecord(record._id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const loadData = () => {
    setIsLoading(true);
    timeTrackerUserGroupsListService()
      .then((res) => {
        setIsLoading(false);
        setDataSource(res.data.items || []);
      })
      .catch((err) => {
        setIsLoading(false);
        handleApiError(err);
      });
  };

  useEffect(() => {
    if (permissions == null) return;
    if (!accessControls.can(permissions, 'TimeTrackerUserGroup.read:any')) return;
    loadData();
  }, [permissions]);

  const deleteRecord = (id) => {
    confirmDelete().then(() => {
      setIsLoading(true);
      timeTrackerUserGroupDeleteService(id)
        .then((res) => {
          setIsLoading(false);
          handleApiSuccess(res);
          loadData();
        })
        .catch((err) => {
          setIsLoading(false);
          handleApiError(err);
        });
    });
  };

  if (permissions == null) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (!accessControls.can(permissions, 'TimeTrackerUserGroup.read:any')) {
    return (
      <div style={{ padding: 24 }}>
        <Result
          status="403"
          title="Access denied"
          subTitle="You do not have permission to view Time Tracker user groups."
          extra={
            <Button type="primary" onClick={() => history.push('/')}>
              Go to Dashboard
            </Button>
          }
        />
      </div>
    );
  }

  const canCreate = accessControls.can(permissions, 'TimeTrackerUserGroup.create:any');

  return (
    <div>
      <PageHeader
        ghost={false}
        title="Time Tracker User Groups"
        subTitle="Create groups and assign them to projects. With Team on each project, that controls access for most users; Super Admin can always join any team and see all projects."
        extra={
          canCreate
            ? [
                <Button
                  key="create"
                  type="primary"
                  icon={<PlusCircleOutlined />}
                  onClick={() => history.push('/time-tracker/user-groups/create')}
                >
                  New group
                </Button>,
              ]
            : []
        }
      />
      <Table
        rowKey="_id"
        loading={isLoading}
        dataSource={dataSource}
        columns={columns}
        pagination={{ pageSize: 20 }}
        size="small"
      />
    </div>
  );
}
